import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { WorkflowProvider, useWorkflow } from '@/contexts/WorkflowContext';
import { SelectionModeProvider } from '@/contexts/SelectionModeContext';
import { DialogProvider, useDialogs } from '@/contexts/DialogContext';
import { EntityDetailsProvider } from '@/contexts/EntityDetailsContext';
import { MobileContextMenuProvider } from '@/contexts/MobileContextMenuContext';
import { MobileContextMenuOverlay } from '@/components/workflow/MobileContextMenuOverlay';
import { EntityDetailsDialogMount } from '@/gui/dialogs/EntityDetailsDialogMount';
import { useIsMobile } from '@/hooks/use-mobile';
import { useOnboardingTelemetryMount } from '@/hooks/useOnboardingTelemetry';
import { usePersistedViewMode } from '@/hooks/usePersistedViewMode';
import { useTranslation } from '@/i18n/LanguageContext';
import { type MinimapData } from '@/components/editor/WorkflowCanvas';
import { IndexMobileShell } from '@/gui/layout/IndexMobileShell';
import { IndexDesktopShell } from '@/gui/layout/IndexDesktopShell';
import { useIndexSidebars } from '@/gui/layout/IndexSidebars';
import { WORKFLOW_PROFILE_KEY } from '@/lib/workflowTemplates';
import { isWizardDismissed } from '@/lib/wizardDismissal';
import { getToolPreset } from '@/lib/toolPresets';
import { mapTemplateToUseCase } from '@/components/onboarding/guideSteps';
import { CrossSheetLinkBanner } from '@/components/workflow/CrossSheetLinkBanner';
import { KeyboardShortcutsDialog } from '@/components/editor/dialogs/KeyboardShortcutsDialog';
import { useAuth } from '@/contexts/AuthContext';
import { usePlan } from '@/hooks/usePlan';
import { useIndexPage } from '@/functions/useIndexPage';
import { IndexDialogs } from '@/gui/dialogs/AllDialogs';
import { IndexConfirmDialogs } from '@/gui/dialogs/ConfirmDialogs';
import { DevStateInspector } from '@/components/dev/DevStateInspector';

const IndexBody = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  // viewOnly = explicit ?mode=view-flag på URL:n. Över-limit imports
  // routas till /preview (separat read-only sida) snarare än att flippa
  // viewOnly här — så det finns ingen jailbreak-vektor från en stäng-banner
  // tillbaka till editing-läge.
  const viewOnly = searchParams.get('mode') === 'view';
  const { t, language } = useTranslation();
  const isMobile = useIsMobile();
  useOnboardingTelemetryMount({ isMobile });
  const { isAuthenticated } = useAuth();
  const { limits } = usePlan();
  const [viewMode, setViewMode] = usePersistedViewMode('fw.viewMode.canvas', '2d');
  const dlg = useDialogs();

  // #46: Splash sign-in CTA + andra externa länkar kan deep-linka hit
  // med ?login=1 för att direkt öppna auth-dialogen. Open-once: vi
  // poppar sedan flaggan från URL:en så reload inte återöppnar dialogen.
  useEffect(() => {
    if (searchParams.get('login') === '1') {
      dlg.setLoginOpen(true);
      const sp = new URLSearchParams(searchParams);
      sp.delete('login');
      navigate(`?${sp.toString()}`, { replace: true });
    }
  }, [searchParams, dlg, navigate]);

  // Workflow-fält som IndexBody:s JSX konsumerar direkt. Handlers och
  // resten av state-monteringen bor i useIndexPage (/functions).
  const {
    blocks, connectors, groups,
    createBlock,
    setWorkflowName,
    loadState, getState,
    canUndo, canRedo,
    sheets, activeSheetIndex, setActiveSheetIndex,
    resetLayerPath,
  } = useWorkflow();

  // Hela handler/state-monteringen — se useIndexPage för innehållet.
  const page = useIndexPage({ viewOnly });

  // UI-lokal state som bara JSX:en bryr sig om.
  const [minimapData, setMinimapData] = useState<MinimapData | null>(null);
  const [shareCoopEnabled, setShareCoopEnabled] = useState(false);

  const toolTypes = getToolPreset(mapTemplateToUseCase(page.workflowProfile?.templateId)).map(p => ({
    type: p.type,
    label: p.label[language] ?? p.label.en,
    icon: p.icon,
  }));

  const { sidebarLeft, sidebarRight } = useIndexSidebars({
    crossSheetMode: page.crossSheetMode,
    onToggleConnectMode: page.handleToggleConnectMode,
    onToggleGroupMode: page.handleToggleGroupMode,
    onToggleCrossSheetMode: page.handleToggleCrossSheetMode,
    // C8: ToolSidebar-klick måste gå genom routed handler så cross-sheet-
    // mode picker:ar target istället för att skriva över selectedBlockId.
    onSelectBlock: page.handleSelectBlockRouted,
    onUpdateBlockColor: page.handleUpdateBlockColor,
    onUpdateGroupColor: page.handleUpdateGroupColor,
    onToggleGroupLock: page.handleToggleGroupLock,
    onToggleGroupCollapse: page.handleToggleGroupCollapse,
    onCollapseAll: page.handleCollapseAll,
    onCollapseToLayers: page.handleCollapseToLayers,
    onExpandAll: page.handleExpandAll,
    onNavigateToLayer: page.handleNavigateToLayer,
    selectedGroup: page.selectedGroup,
    onDeleteBlockRequested: () => dlg.setConfirmDeleteBlockOpen(true),
    onDeleteGroupRequested: () => dlg.setConfirmDeleteGroupOpen(true),
    minimapData,
    onNavigateToGroup: page.handleNavigateToLayer,
    onStartCrossSheetLink: page.startCrossSheetFromBlock,
    currentDocumentId: page.currentDocumentId,
  });

  return (
    <div className="h-svh flex flex-col overflow-hidden">
      {/* Enterprise cross-sheet linking banner — fixed overlay, only rendered
          when the mode is active. Guides the user through source → switch
          sheet → target and offers a quick-switch control for the two
          adjacent sheets (the only valid targets). */}
      <CrossSheetLinkBanner
        active={page.crossSheetMode}
        sheets={sheets}
        activeSheetIndex={activeSheetIndex}
        source={page.crossSheetSource}
        onCancel={page.handleToggleCrossSheetMode}
        onSwitchSheet={setActiveSheetIndex}
      />
      {/* Tidigare: `isMobile && !viewOnly` — vilket gav DESKTOP-shell på
          mobil för delade view-only-länkar. Nu får alla mobile-users
          MobileShell oavsett viewOnly; båda shells respekterar viewOnly-
          prop och döljer mutativa actions korrekt. */}
      {isMobile ? (
        <IndexMobileShell
          selectedGroup={page.selectedGroup}
          isConnected={page.isConnected} connectionDebugInfo={page.connectionDebugInfo}
          isDirty={page.isDirty}
          announcement={page.announcement}
          canUndo={canUndo} canRedo={canRedo}
          canvasInnerRef={page.canvasInnerRef}
          onSelectBlock={page.handleSelectBlockRouted} onSelectGroup={page.handleSelectGroup}
          onCanvasClick={page.handleCanvasClick}
          onCreateBlock={createBlock}
          onUpdateBlockIcon={page.handleUpdateBlockIcon}
          onConnect={page.handleConnect}
          onToggleGroupSelection={page.handleToggleGroupSelection}
          onToggleConnectMode={page.handleToggleConnectMode}
          onToggleGroupMode={page.handleToggleGroupMode}
          onUpdateGroupPosition={page.handleUpdateGroupPosition}
          onConvertToLayer={page.handleConvertToLayer}
          onNavigateToLayer={page.handleNavigateToLayer}
          onDeleteBlock={() => dlg.setConfirmDeleteBlockOpen(true)}
          onDeleteGroup={() => dlg.setConfirmDeleteGroupOpen(true)}
          onSave={page.handleSave} onLoad={page.handleLoad} onExportPNG={page.handleExportPNG}
          onSimulate={page.handleSimulate} onClear={page.handleClearRequest}
          onImportJson={() => dlg.setImportOpen(true)}
          onCopyJson={dlg.openExportJson}
          onHelp={() => dlg.setHelpOpen(true)}
          onAiJson={() => dlg.setAiJsonOpen(true)}
          onAiAssistant={() => dlg.setAiAssistantOpen(true)}
          onPreview={() => dlg.setOverviewOpen(true)}
          onLogin={() => dlg.setLoginOpen(true)}
          onCloudWorkspace={() => dlg.setCloudWorkspaceOpen(true)}
          onShareWorkspace={() => dlg.setShareWorkspaceOpen(true)}
          onFeatureRequest={() => dlg.setFeatureRequestOpen(true)}
          onBugReport={() => dlg.setBugReportOpen(true)}
          onDonate={() => window.open('https://paypal.me/arentb', '_blank', 'noopener,noreferrer')}
          onUndo={page.handleUndo} onRedo={page.handleRedo}
          mobileGestures={page.mobileGestures}
          onWizardComplete={page.handleWizardComplete}
        />
      ) : (
        <IndexDesktopShell
          viewOnly={viewOnly}
          crossSheetMode={page.crossSheetMode} crossSheetSource={page.crossSheetSource}
          currentDocumentId={page.currentDocumentId}
          isConnected={page.isConnected} connectionDebugInfo={page.connectionDebugInfo}
          isCoopLoading={page.isCoopLoading}
          isDirty={page.isDirty}
          realtime={page.realtime}
          limits={limits}
          aiPreview={page.aiPreview} aiPreviewPending={page.aiPreviewPending}
          regressionDialog={page.regressionDialog}
          explainBlockId={page.explainBlockId}
          viewMode={viewMode} setViewMode={setViewMode}
          actionLogEntries={page.actionLogEntries}
          canvasInnerRef={page.canvasInnerRef}
          toolTypes={toolTypes}
          setMinimapData={setMinimapData}
          announcement={page.announcement}
          isAuthenticated={isAuthenticated}
          onNewWorkflowConfirm={page.handleNewWorkflowCreate}
          canRunProcesses={limits.canRunProcesses}
          canUseCoop={limits.maxCoopWorkspaces !== 0}
          {...page.topBarHandlers}
          onProcessMotor={page.currentDocumentId ? () => dlg.setProcessMotorOpen(true) : undefined}
          onReopenWizard={() => dlg.setWizardOpen(true)}
          onFeatureRequest={() => dlg.setFeatureRequestOpen(true)}
          onBugReport={() => dlg.setBugReportOpen(true)}
          onDonate={() => window.open('https://paypal.me/arentb', '_blank', 'noopener,noreferrer')}
          onUndo={page.handleUndo} onRedo={page.handleRedo}
          canUndo={canUndo} canRedo={canRedo}
          onApplyAiProposal={page.handleApplyAiProposal}
          onDiscardAiProposal={page.handleDiscardAiProposal}
          onRegressionApplyAnyway={page.handleRegressionApplyAnyway}
          onRegressionRetry={page.handleRegressionRetry}
          onRegressionCancel={page.handleRegressionCancel}
          onRegressionOpenChange={(o) => { if (!o) page.setRegressionDialog({ open: false, result: null }); }}
          onExplainOpenChange={(o) => { if (!o) page.setExplainBlockId(null); }}
          onWizardComplete={page.handleWizardComplete}
          onClearActionLog={page.clearActionLog}
          mobileGestures={page.mobileGestures}
          onSelectBlock={page.handleSelectBlockRouted} onSelectGroup={page.handleSelectGroup}
          onCreateBlock={page.onCreateBlock}
          onUpdateBlockIcon={page.handleUpdateBlockIcon}
          onConnect={page.handleConnect}
          onToggleGroupSelection={page.handleToggleGroupSelection}
          onCanvasClick={page.handleCanvasClick}
          onUpdateGroupPosition={page.handleUpdateGroupPosition}
          onConvertToLayer={page.handleConvertToLayer}
          onFocusBlockProperty={page.handleFocusBlockProperty}
          onCreateGroupFromSelection={page.handleCreateGroupFromIds}
          onAddBlockToGroup={page.handleAddBlockToGroup}
          onDuplicateBlock={page.handleDuplicateBlock}
          onExplainBlock={page.handleExplainBlock}
          onDeleteBlock={page.handleDeleteBlockById}
          onToggleGroupCollapse={page.handleToggleGroupCollapse}
          onToggleGroupLock={page.handleToggleGroupLock}
          sidebarLeft={sidebarLeft}
          sidebarRight={sidebarRight}
          statusBarZoom={minimapData?.zoom}
        />
      )}

      <IndexDialogs
        currentDocumentId={page.currentDocumentId}
        onSaveToDb={page.handleSaveToDb}
        onList={page.listDocuments} onLoad={page.handleLoadFromDb} onDelete={page.handleDeleteFromDb}
        onImport={page.handleImportJson}
        importLimits={limits}
        onStageAiProposal={page.handleStageAiProposal}
        onViewIn3D={() => setViewMode('3d')}
        aiHoningPrompt={page.workflowProfile?.aiHoningPrompt}
        shareCoopEnabled={shareCoopEnabled}
        onShareToggleCoop={async (enabled) => {
          if (!page.currentDocumentId) return false;
          const ok = await page.setCoopEnabled(page.currentDocumentId, enabled);
          if (ok) setShareCoopEnabled(enabled);
          return ok;
        }}
        onCloudLoad={(state, name, docId) => {
          // Gate: routar over-limit-laddning till /preview innan vi rör
          // huvud-canvas. Free-tier user som tidigare sparade som paid
          // måste annars kunna redigera 50-block-flödet de inte har
          // license för längre.
          if (page.checkAndRouteIfOverLimit(state)) return;
          loadState(state);
          setWorkflowName(name);
          page.setCurrentDocumentId(docId);
          resetLayerPath();
        }}
        onExportView={page.handleExportView} onExportAll={page.handleExportAll}
        getActionLogSnapshot={() => page.snapshotActionLog()}
        getWorkflowStats={() => ({ blocks: blocks.length, connectors: connectors.length, groups: groups.length })}
      />

      <KeyboardShortcutsDialog
        open={dlg.shortcutsOpen}
        onOpenChange={dlg.setShortcutsOpen}
      />

      <IndexConfirmDialogs
        onClearConfirmed={page.handleClearConfirmed}
        clearLabel={t('clear')}
      />

      <EntityDetailsDialogMount currentDocumentId={page.currentDocumentId} />

      <MobileContextMenuOverlay
        onFocusBlockProperty={page.handleFocusBlockProperty}
        onConvertToLayer={page.handleConvertToLayer}
        onDuplicateBlock={page.handleDuplicateBlock}
        onDeleteBlock={page.handleDeleteBlockById}
        onDeleteGroup={() => dlg.setConfirmDeleteGroupOpen(true)}
        onToggleGroupCollapse={page.handleToggleGroupCollapse}
        onToggleGroupLock={page.handleToggleGroupLock}
        toolTypes={toolTypes}
        onAddBlock={(type, worldX, worldY) => page.onCreateBlock(type, worldX, worldY)}
      />
      {/* #17: dev-only — Ctrl/Cmd+Shift+D togglar state-panelen.
          Renderar null i production-byggen. */}
      <DevStateInspector />
    </div>
  );
};

/**
 * Top-level page export — provides Workflow + SelectionMode + Dialog
 * contexts so IndexBody and all descendants (sidebars, canvas, dialogs,
 * shells) can read app state via hooks instead of drilled props.
 *
 * `initialWizardOpen` is computed here so the wizard auto-opens for
 * fresh sessions but stays closed for view-only links.
 */
const Index = () => {
  const [searchParams] = useSearchParams();
  const initialViewOnly = searchParams.get('mode') === 'view';
  // If user previously checked "Remember my choices", a saved workflow
  // profile exists in localStorage — skip auto-opening the wizard.
  const hasSavedProfile = (() => {
    try { return !!localStorage.getItem(WORKFLOW_PROFILE_KEY); } catch { return false; }
  })();
  // #32: även en explicit stängning (utan complete) ska respekteras —
  // flaggan skrivs i useIndexPage när wizardOpen går true→false.
  const wizardDismissed = isWizardDismissed();
  return (
    <WorkflowProvider>
      <SelectionModeProvider>
        <DialogProvider initialWizardOpen={!initialViewOnly && !hasSavedProfile && !wizardDismissed}>
          <EntityDetailsProvider>
            <MobileContextMenuProvider>
              <IndexBody />
            </MobileContextMenuProvider>
          </EntityDetailsProvider>
        </DialogProvider>
      </SelectionModeProvider>
    </WorkflowProvider>
  );
};

export default Index;
